import type { StorageOverview } from '@obras/shared';
import { formatBytes, formatDate, formatPercent } from '@obras/shared';
import { useQuery } from '@tanstack/react-query';
import { HardDrive } from 'lucide-react';

import { PageHeader } from '../components/AppShell.js';
import { Card, CardHeader, Kpi } from '../components/ui/Card.js';
import { EmptyState, ErrorState, Skeleton } from '../components/ui/Feedback.js';
import { api } from '../lib/api.js';
import { cn } from '../lib/cn.js';

function useArmazenamento() {
  return useQuery({
    queryKey: ['consumo', 'armazenamento'],
    queryFn: ({ signal }) => api.get<StorageOverview>('/consumo/armazenamento', signal),
  });
}

/** Barra de proporção, em fatias. Uma linha só, sem legenda flutuante. */
function Fatias({
  partes,
  total,
}: {
  partes: readonly { rotulo: string; bytes: number }[];
  total: number;
}) {
  const tons = ['bg-ink', 'bg-ink/55', 'bg-gray-data'];

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-sunken">
        {partes.map((parte, indice) => (
          <span
            key={parte.rotulo}
            className={cn('h-full', tons[indice] ?? 'bg-gray-data')}
            style={{ width: total > 0 ? `${(parte.bytes / total) * 100}%` : '0%' }}
          />
        ))}
      </div>

      <ul className="flex flex-wrap gap-x-5 gap-y-1.5 text-[12px]">
        {partes.map((parte, indice) => (
          <li key={parte.rotulo} className="flex items-center gap-2">
            <span
              className={cn('size-2 shrink-0 rounded-full', tons[indice] ?? 'bg-gray-data')}
              aria-hidden
            />
            <span>{parte.rotulo}</span>
            <span className="tabular text-ink-3">{formatBytes(parte.bytes)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Lista com barra proporcional ao maior — comparação sem eixo nem número solto. */
function Ranking({
  titulo,
  descricao,
  itens,
  vazio,
}: {
  titulo: string;
  descricao: string;
  itens: readonly { chave: string; nome: string; bytes: number; detalhe: string }[];
  vazio: string;
}) {
  const maior = Math.max(1, ...itens.map((item) => item.bytes));

  return (
    <Card className="overflow-hidden">
      {/*
       * Sem lista, o cartão é a explicação: repetir a descrição acima de um
       * "nada aqui" seria dizer duas vezes o que não aconteceu.
       */}
      <CardHeader title={titulo} description={itens.length === 0 ? vazio : descricao} />

      {itens.length === 0 ? null : (
        <ul className="flex flex-col gap-3 px-5 pb-5">
          {itens.map((item) => (
            <li key={item.chave} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-[13px]">{item.nome}</span>
                <span className="tabular shrink-0 text-[13px] font-medium">
                  {formatBytes(item.bytes)}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-sunken">
                  <span
                    className="block h-full rounded-full bg-ink"
                    style={{ width: `${Math.max((item.bytes / maior) * 100, 2)}%` }}
                  />
                </span>
                <span className="shrink-0 text-[11px] text-ink-3">{item.detalhe}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Consumo do espaço em nuvem.
 *
 * Responde a uma pergunta: **onde está o espaço?** Quanto do contratado já foi
 * usado, o que ocupa, quem enviou e quais arquivos são grandes demais.
 *
 * O nome é o assunto: "Gestão" já era o título do grupo no menu, e um item
 * chamado igual ao grupo que o contém não diz a ninguém o que vai encontrar.
 *
 * Deliberadamente curta. Indicador de obra tem tela própria — repeti-los aqui
 * faria duas telas medianas no lugar de duas boas, e enterraria a única
 * informação que só existe nesta.
 */
export function ConsumoPage() {
  const { data, isPending, isError, error, refetch } = useArmazenamento();

  return (
    <>
      <PageHeader
        title="Consumo"
        description="Espaço em nuvem do órgão: quanto foi usado, por quem e com o quê."
      />

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <div className="flex flex-col gap-6">
          <div className="grid gap-3 sm:grid-cols-3">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-[118px] rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-52 rounded-lg" />
        </div>
      ) : data.files === 0 ? (
        <Card>
          <EmptyState
            icon={HardDrive}
            title="Nada no acervo"
            description="Fotos, documentos e vídeos enviados nos relatórios aparecem aqui."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <Kpi
              label="Espaço em uso"
              value={formatBytes(data.usedBytes)}
              hint={`${formatPercent(data.limitBytes > 0 ? data.usedBytes / data.limitBytes : 0, 1)} de ${formatBytes(data.limitBytes)}`}
              tone={data.freeBytes === 0 ? 'danger' : 'default'}
            />
            <Kpi label="Espaço livre" value={formatBytes(data.freeBytes)} />
            <Kpi label="Arquivos" value={String(data.files)} tone="ink" />
          </div>

          <Card className="overflow-hidden">
            <CardHeader
              title="O que ocupa o espaço"
              description="Vídeo pesa mais que foto por ordens de grandeza — é onde o espaço some primeiro."
            />
            <div className="px-5 pb-5">
              <Fatias
                partes={data.byKind.map((parte) => ({ rotulo: parte.kind, bytes: parte.bytes }))}
                total={data.usedBytes}
              />
            </div>
          </Card>

          <div className="grid items-start gap-3 lg:grid-cols-2">
            <Ranking
              titulo="Quem mais enviou"
              descricao="Por peso, não por quantidade — o que ocupa espaço é o tamanho do arquivo."
              itens={data.byUser.map((linha) => ({
                chave: linha.userId,
                nome: linha.name,
                bytes: linha.bytes,
                detalhe: `${linha.files} arquivo${linha.files === 1 ? '' : 's'}`,
              }))}
              vazio="Todo o acervo veio do sistema antigo, que não registrava quem enviava. Os envios daqui em diante aparecem aqui, por peso."
            />

            <Ranking
              titulo="Obras que mais ocupam"
              descricao="Somando as fotos e os vídeos dos relatórios de cada obra."
              itens={data.byObra.map((linha) => ({
                chave: linha.obraId,
                nome: linha.name,
                bytes: linha.bytes,
                detalhe: `${linha.files} arquivo${linha.files === 1 ? '' : 's'}`,
              }))}
              vazio="Nenhum arquivo vinculado a obra."
            />
          </div>

          <Card className="overflow-hidden">
            <CardHeader
              title="Maiores arquivos"
              description="Quando o espaço aperta, é por aqui que se começa."
            />
            <div className="overflow-x-auto px-5 pb-5">
              <table className="tabular w-full min-w-[520px] text-[13px]">
                <thead>
                  <tr className="text-left text-ink-3 *:border-b-2 *:border-line-2">
                    <th className="py-1.5 font-medium">Arquivo</th>
                    <th className="py-1.5 font-medium">Enviado por</th>
                    <th className="py-1.5 text-right font-medium">Tamanho</th>
                    <th className="py-1.5 text-right font-medium">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.largest.map((arquivo) => (
                    <tr key={arquivo.id}>
                      <td className="max-w-72 truncate py-2">{arquivo.name}</td>
                      <td className="max-w-40 truncate py-2 text-ink-3">
                        {arquivo.uploadedBy ?? '—'}
                      </td>
                      <td className="py-2 text-right font-medium">
                        {formatBytes(arquivo.sizeBytes)}
                      </td>
                      <td className="py-2 text-right font-mono text-[11px] text-ink-3">
                        {formatDate(arquivo.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
